import type { ScheduleSlotRequest } from "@gettactic/api";

export interface RoomReservationSlotPeriod {
  start: string;
  end: string;
  time_zone: string;
}

export interface BuildRoomReservationSlotsParams {
  /** Room ids of the reservation, in display order. Duplicates are ignored. */
  roomIds: string[];
  /** Attendee ids or emails. Duplicates are ignored. */
  users: string[];
  period: RoomReservationSlotPeriod;
  /** Splits a user id/email into the `user_id` / `email` pair the API expects. */
  toSlotIdentifier: (userIdOrEmail: string) => {
    user_id: string | null;
    email?: string | null;
  };
}

const unique = (values: string[]): string[] => Array.from(new Set(values));

/**
 * Builds the slots of a room reservation as the cartesian product of its rooms and its
 * attendees. The API requires every user of a reservation to hold every one of its
 * resources — a user present on one room but missing on another is rejected as an
 * inconsistent reservation — so a booking of 2 rooms for 3 people is 6 slots.
 */
export const buildRoomReservationSlots = ({
  roomIds,
  users,
  period,
  toSlotIdentifier
}: BuildRoomReservationSlotsParams): ScheduleSlotRequest[] => {
  const slots: ScheduleSlotRequest[] = [];

  for (const roomId of unique(roomIds)) {
    for (const user of unique(users)) {
      slots.push({
        ...toSlotIdentifier(user),
        resource_id: roomId,
        team_id: null,
        slots: 1,
        start: period.start,
        end: period.end,
        time_zone: period.time_zone
      });
    }
  }

  return slots;
};
